import { Controller, useFormContext } from "react-hook-form";

import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { VcsCheck } from "types/generated";
import FormToggleField from "ds/components/Form/ToggleField";

import { VCSCheckLabels } from "../../components/Details/helpers";

const VCSChecksToggle = () => {
  const { watch, control, setValue } = useFormContext();
  const currentValue = watch("vcsChecks");
  const individualFailuresOnly = watch("individualFailuresOnly");
  const individualSkipUnchanged = watch("individualSkipUnchanged");

  const isIndividualEnabled = currentValue === VcsCheck.Individual || currentValue === VcsCheck.All;

  const handleAggregatedChange = (type: VcsCheck) => () => {
    if (currentValue === type || currentValue === VcsCheck.All) {
      if (type === VcsCheck.Individual) {
        setValue("vcsChecks", VcsCheck.Aggregated, { shouldDirty: true });
        setValue("individualFailuresOnly", false, { shouldDirty: true });
        setValue("individualSkipUnchanged", false, { shouldDirty: true });
      } else {
        setValue("vcsChecks", VcsCheck.Individual, { shouldDirty: true });
      }
    } else {
      setValue("vcsChecks", VcsCheck.All, { shouldDirty: true });
    }
  };

  const handleIndividualFailuresOnlyChange = () => {
    setValue("individualFailuresOnly", !individualFailuresOnly, { shouldDirty: true });
  };

  const handleIndividualSkipUnchangedChange = () => {
    setValue("individualSkipUnchanged", !individualSkipUnchanged, { shouldDirty: true });
  };

  return (
    <Controller
      name="vcsChecks"
      control={control}
      render={() => (
        <FormField
          label="VCS checks"
          tooltipInfo={
            <TooltipModalBody align="start">
              At least one option should be selected
            </TooltipModalBody>
          }
        >
          <Box gap="medium" direction="column">
            <FormToggleField
              variant="switch"
              align="center"
              onChange={handleAggregatedChange(VcsCheck.Individual)}
              checked={isIndividualEnabled}
              title={VCSCheckLabels[VcsCheck.Individual]}
              description="One VCS check for each stack"
            />
            {isIndividualEnabled && (
              <Box padding="0 0 0 large">
                <FormToggleField
                  variant="switch"
                  align="center"
                  onChange={handleIndividualFailuresOnlyChange}
                  checked={individualFailuresOnly}
                  title="Create check on failures only"
                  description="Only send individual check runs when a run reaches a non-success terminal state"
                />
              </Box>
            )}
            {isIndividualEnabled && (
              <Box padding="0 0 0 large">
                <FormToggleField
                  variant="switch"
                  align="center"
                  onChange={handleIndividualSkipUnchangedChange}
                  checked={individualSkipUnchanged}
                  title="Skip unchanged stacks"
                  description="Don't create individual checks for stacks that have no changes"
                />
              </Box>
            )}
            <FormToggleField
              variant="switch"
              align="center"
              onChange={handleAggregatedChange(VcsCheck.Aggregated)}
              checked={currentValue === VcsCheck.Aggregated || currentValue === VcsCheck.All}
              title={VCSCheckLabels[VcsCheck.Aggregated]}
              description="Summarize checks across all affected stacks into a predefined set"
            />
          </Box>
        </FormField>
      )}
    />
  );
};

export default VCSChecksToggle;
